/**
 * A paleta de tiles do editor de mapas.
 *
 * Até aqui o editor só anotava: dava para dizer que uma casa é floresta, mas
 * não para desenhar a floresta. Esta paleta é a metade que faltava — os mesmos
 * tiles que o acervo usa, tirados das folhas do tileset do mapa aberto, no
 * mesmo agrupamento do RPG Maker (A1–A5 e B–E).
 *
 * Cada miniatura é desenhada com a MESMA função que desenha o mapa. Recortar a
 * folha por conta própria daria uma paleta que mostra uma coisa e pinta outra
 * assim que um autotile entrasse na história.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ASSETS_BASE, type Manifest } from '../assets/manifest.js';
import type { TilesetDef } from '../render/tilemap.js';
import {
  TILE_ID_A1,
  TILE_ID_A2,
  TILE_ID_A3,
  TILE_ID_A4,
  TILE_ID_A5,
  TILE_SIZE,
  tilePieces,
  type SheetNumber,
} from '../render/tiles.js';

/** Um grupo da paleta, na divisão que o RPG Maker usa. */
interface TileGroup {
  readonly id: string;
  readonly label: string;
  readonly hint: string;
  /** Índice da folha dentro do tileset (0=A1 … 8=E). */
  readonly sheet: SheetNumber;
  readonly tiles: readonly number[];
}

/** Autotiles ocupam um bloco inteiro da folha; a paleta mostra um por tipo. */
function autotileGroup(
  id: string,
  label: string,
  hint: string,
  sheet: SheetNumber,
  firstTileId: number,
  kinds: number,
): TileGroup {
  return {
    id,
    label,
    hint,
    sheet,
    // A forma 0 é o tile isolado do tipo: serve de amostra, e a forma real sai
    // do cálculo de vizinhança na hora de pintar.
    tiles: Array.from({ length: kinds }, (_, k) => firstTileId + k * 48),
  };
}

function normalGroup(
  id: string,
  label: string,
  hint: string,
  sheet: SheetNumber,
  first: number,
  count: number,
): TileGroup {
  return { id, label, hint, sheet, tiles: Array.from({ length: count }, (_, i) => first + i) };
}

/**
 * Os grupos que este tileset realmente tem.
 *
 * Um tileset sem folha A3 não pode oferecer paredes: o slot vem vazio do acervo
 * e cada miniatura sairia em branco.
 */
function groupsFor(tileset: TilesetDef): TileGroup[] {
  const has = (index: number): boolean => Boolean(tileset.sheets[index]);
  const groups: TileGroup[] = [];

  if (has(0)) groups.push(autotileGroup('A1', 'A1 · Água', 'superfícies animadas', 0, TILE_ID_A1, 16));
  if (has(1)) groups.push(autotileGroup('A2', 'A2 · Terreno', 'grama, terra, piso', 1, TILE_ID_A2, 32));
  if (has(2)) groups.push(autotileGroup('A3', 'A3 · Paredes', 'fachadas e telhados', 2, TILE_ID_A3, 32));
  if (has(3)) groups.push(autotileGroup('A4', 'A4 · Muros', 'paredes altas', 3, TILE_ID_A4, 40));
  if (has(4)) groups.push(normalGroup('A5', 'A5 · Chão', 'piso avulso', 4, TILE_ID_A5, 128));
  if (has(5)) groups.push(normalGroup('B', 'B · Objetos', 'móveis e detalhes', 5, 0, 256));
  if (has(6)) groups.push(normalGroup('C', 'C · Objetos', 'móveis e detalhes', 6, 256, 256));
  if (has(7)) groups.push(normalGroup('D', 'D · Objetos', 'móveis e detalhes', 7, 512, 256));
  if (has(8)) groups.push(normalGroup('E', 'E · Objetos', 'móveis e detalhes', 8, 768, 256));

  return groups;
}

/** Desenha um tile numa miniatura, com as folhas já carregadas. */
function TileButton({
  tileId,
  sheets,
  selected,
  onPick,
}: {
  readonly tileId: number;
  readonly sheets: ReadonlyMap<SheetNumber, HTMLImageElement>;
  readonly selected: boolean;
  readonly onPick: () => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = TILE_SIZE;
    canvas.height = TILE_SIZE;
    ctx.clearRect(0, 0, TILE_SIZE, TILE_SIZE);
    ctx.imageSmoothingEnabled = false;

    for (const piece of tilePieces(tileId, 0, 0)) {
      const image = sheets.get(piece.sheet);
      if (!image) continue;
      // Um recorte fora da folha desenha nada e engole o erro; conferir aqui
      // deixa o buraco visível como buraco, e não como tile "quase certo".
      if (piece.sx + piece.sw > image.naturalWidth || piece.sy + piece.sh > image.naturalHeight) {
        continue;
      }
      ctx.drawImage(
        image,
        piece.sx, piece.sy, piece.sw, piece.sh,
        piece.dx, piece.dy, piece.sw, piece.sh,
      );
    }
  }, [tileId, sheets]);

  return (
    <button
      type="button"
      className={`tile-cell ${selected ? 'selected' : ''}`}
      onClick={onPick}
      title={`Tile ${tileId}`}
      aria-pressed={selected}
    >
      <canvas ref={canvasRef} />
    </button>
  );
}

export function TilePalette({
  manifest,
  tileset,
  selectedTile,
  onSelect,
}: {
  readonly manifest: Manifest;
  readonly tileset: TilesetDef;
  readonly selectedTile: number;
  readonly onSelect: (tileId: number) => void;
}): JSX.Element {
  const groups = useMemo(() => groupsFor(tileset), [tileset]);
  const [groupId, setGroupId] = useState<string>(groups[0]?.id ?? 'A2');
  const [sheets, setSheets] = useState<ReadonlyMap<SheetNumber, HTMLImageElement>>(new Map());

  // Trocar de mapa pode trocar o tileset: um grupo que não existe mais deixaria
  // a paleta vazia sem dizer por quê.
  useEffect(() => {
    if (!groups.some((g) => g.id === groupId)) setGroupId(groups[0]?.id ?? 'A2');
  }, [groups, groupId]);

  useEffect(() => {
    let cancelled = false;
    const loaded = new Map<SheetNumber, HTMLImageElement>();

    const jobs = tileset.sheets.map(
      (name, index) =>
        new Promise<void>((resolve) => {
          if (!name) return resolve();
          const entry = manifest.images['tilesets']?.[name];
          if (!entry) return resolve();
          const image = new Image();
          image.onload = () => {
            loaded.set(index as SheetNumber, image);
            resolve();
          };
          image.onerror = () => resolve();
          image.src = `${ASSETS_BASE}/img/tilesets/${entry.file}`;
        }),
    );

    void Promise.all(jobs).then(() => {
      if (!cancelled) setSheets(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [manifest, tileset]);

  const group = groups.find((g) => g.id === groupId) ?? groups[0];

  return (
    <div className="tile-palette">
      <div className="palette-groups">
        {groups.map((g) => (
          <button
            key={g.id}
            type="button"
            className={`palette-group-btn ${g.id === groupId ? 'active' : ''}`}
            onClick={() => setGroupId(g.id)}
            title={g.hint}
          >
            {g.label}
          </button>
        ))}
      </div>

      {sheets.size === 0 ? (
        <p className="small">carregando folhas do tileset…</p>
      ) : group ? (
        <>
          <p className="small">
            {group.hint}
            {group.id.startsWith('A') && group.id !== 'A5'
              ? ' · as bordas se encaixam sozinhas ao pintar'
              : ''}
          </p>
          <div className="tile-grid">
            {group.tiles.map((tileId) => (
              <TileButton
                key={tileId}
                tileId={tileId}
                sheets={sheets}
                selected={tileId === selectedTile}
                onPick={() => onSelect(tileId)}
              />
            ))}
          </div>
        </>
      ) : (
        <p className="small">Este tileset não traz folhas de tiles.</p>
      )}
    </div>
  );
}
